<!-- payment-form.component.html -->
<mat-card class="payment-card">
  <div class="card-header">
    <mat-card-title>Pay Your Balance</mat-card-title>
    <div class="help-icons">
      <mat-icon class="help-icon" (click)="callClinic()">phone-link</mat-icon>
      <mat-icon class="help-icon" (click)="emailClinic()">email-link</mat-icon>
    </div>    
  </div>

  <mat-card-content>
    <!-- Payment summary context -->
    <div class="payment-context">
      <ng-container *ngIf="patientBalance > 0">
        <p class="context-message">
          <mat-icon class="info-icon">info</mat-icon>
          You're making a payment toward your current balance of 
          <strong>{{ patientBalance | currency }}</strong>
        </p>
      </ng-container>
    </div>

    <form [formGroup]="paymentForm" (ngSubmit)="onSubmit()" novalidate>
      <!-- Amount to Pay -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Amount</mat-label>
        <input
          matInput
          formControlName="amount"
          required
          type="number"
          step="0.01"
          placeholder="50.00"
          autocomplete="off"
        />
        <mat-icon matSuffix>attach_money</mat-icon>
        <mat-hint align="end" *ngIf="patientBalance > 0">
          <a (click)="fillFullAmount()" class="action-link">Pay full amount</a>
        </mat-hint>
        <mat-error
          *ngIf="paymentForm.get('amount')?.invalid && paymentForm.get('amount')?.touched"
        >
          <span *ngIf="paymentForm.get('amount')?.hasError('required')">Amount is required</span>
          <span *ngIf="paymentForm.get('amount')?.hasError('pattern')">Please enter a valid amount</span>
          <span *ngIf="paymentForm.get('amount')?.hasError('max')">Amount cannot exceed your balance</span>
        </mat-error>
      </mat-form-field>

      <!-- Payment method section -->
      <div class="section-divider">
        <span>Payment Method</span>
      </div>

      <!-- Card Number -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Card Number</mat-label>
        <input
          matInput
          [type]="showCardNumber ? 'text' : 'password'"
          placeholder="4242 4242 4242 4242"
          autocomplete="cc-number"
          formControlName="cardNumber"
          required
          paymentCardType
          [enableClass]="true"
        />
        <!-- Eye icon to toggle visibility -->
        <button
          mat-icon-button
          matSuffix
          type="button"
          (click)="toggleShowCardNumber()"
          aria-label="Toggle card number visibility"
        >
          <mat-icon>{{ showCardNumber ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
        <span matSuffix class="card-icon-container"></span>

        <mat-error
          *ngIf="paymentForm.get('cardNumber')?.invalid && paymentForm.get('cardNumber')?.touched"
        >
          <span *ngIf="paymentForm.get('cardNumber')?.hasError('required')">Card number is required</span>
          <span *ngIf="paymentForm.get('cardNumber')?.hasError('luhnInvalid')">Invalid card number</span>
        </mat-error>
      </mat-form-field>

      <!-- Expiry & CVC in one row -->
      <div class="row-fields">
        <!-- Expiry Date -->
        <mat-form-field appearance="outline" class="exp-field">
          <mat-label>MM/YY</mat-label>
          <input
            matInput
            placeholder="03/25"
            autocomplete="cc-exp"
            formControlName="expDate"
            paymentCardExpiry
            required
          />
          <mat-error
            *ngIf="paymentForm.get('expDate')?.invalid && paymentForm.get('expDate')?.touched"
          >
            <span *ngIf="paymentForm.get('expDate')?.hasError('required')">Expiry date is required</span>
            <span *ngIf="paymentForm.get('expDate')?.hasError('invalidExpiry')">Invalid format</span>
            <span *ngIf="paymentForm.get('expDate')?.hasError('expired')">Card has expired</span>
          </mat-error>
        </mat-form-field>

        <!-- CVC -->
        <mat-form-field appearance="outline" class="cvc-field">
          <mat-label>CVC</mat-label>
          <input
            matInput
            placeholder="123"
            autocomplete="cc-csc"
            formControlName="cardCode"
            required
          />
          <mat-hint align="end">3 or 4 digits</mat-hint>
          <mat-error
            *ngIf="paymentForm.get('cardCode')?.invalid && paymentForm.get('cardCode')?.touched"
          >
            CVC is required
          </mat-error>
        </mat-form-field>
      </div>

      <!-- Billing information section -->
      <div class="section-divider">
        <span>Billing Information</span>
      </div>

      <!-- Name on Card -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Name on Card</mat-label>
        <input
          matInput
          placeholder="Jane Doe"
          autocomplete="cc-name"
          formControlName="cardholderName"
          required
        />
        <mat-error
          *ngIf="
            paymentForm.get('cardholderName')?.invalid &&
            paymentForm.get('cardholderName')?.touched
          "
        >
          Name is required (2-250 chars)
        </mat-error>
      </mat-form-field>

      <!-- Billing ZIP -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Billing ZIP</mat-label>
        <input
          matInput
          placeholder="90210"
          autocomplete="postal-code"
          formControlName="billingZip"
          usZipcodeFormat
          required
        />
        <mat-error
          *ngIf="
            paymentForm.get('billingZip')?.invalid &&
            paymentForm.get('billingZip')?.touched
          "
        >
          ZIP is required (5-10 chars)
        </mat-error>
      </mat-form-field>

      <!-- Payment processing message -->
      <div class="payment-processing-note">
        <mat-icon>lock</mat-icon>
        <small>
          Your payment information is encrypted and processed securely.
          We do not store your card details.
        </small>
      </div>

      <!-- Pay Button -->
      <div class="submit-row">
        <button
          mat-raised-button
          color="primary"
          type="submit"
          [disabled]="paymentForm.invalid || isProcessing"
          class="pay-button"
        >
          <mat-icon *ngIf="!isProcessing">payment</mat-icon>
          <mat-progress-spinner *ngIf="isProcessing" [diameter]="24" mode="indeterminate"></mat-progress-spinner>
          <span>{{ isProcessing ? 'Processing...' : 'Pay Now' }}</span>
        </button>
      </div>
    </form>
  </mat-card-content>
</mat-card>